import React, { useEffect, useRef, useState } from 'react';
import ModalWrapper from './modal/ModalWrapper';
import AddMember from './modal/AddMember/AddMember';
import ApplyMultipleLicenses from './modal/ApplyMultipleLicenses'
import ApplyAppointmentTypes from './modal/ApplyAppointmentTypes'
import MembersTable from './MembersTable'
import SearchIcon from '@material-ui/icons/Search';
import { Paper, Grid, Button, Typography } from '@material-ui/core'
import MemberFilterDrawer from './MemberFilterDrawer'
import InputBase from '@material-ui/core/InputBase';
import { useDispatch } from 'react-redux';
import { exportMembers, importMembers } from '../actions/member';

const MembersSearchBar = ({onApplySearch}) => {
  const [quickSearch, setSearch] = useState('')

  const onChangeSearchInput = value => setSearch(value)
  const onSubmitSearch = event => {
    event.preventDefault()
    onApplySearch(quickSearch)
  }
  
  return(
    <Grid component="form" onSubmit={onSubmitSearch} container spacing={3}  alignItems="center" >
      <Paper style={{padding: "5px"}}>
        <Grid container spacing={3}  alignItems="center"  >
          <Grid item xs={2} ><SearchIcon /></Grid>
          <Grid item xs={10}>
            <InputBase
              placeholder="Search Members"
              inputProps={{ 'aria-label': 'search members' }}
              onChange={(e) => onChangeSearchInput(e.target.value)}
              value={quickSearch}
            />
          </Grid>
        </Grid>
      </Paper>
      <Grid item> <Button type="submit" variant="contained">Search</Button> </Grid>
    </Grid>
  )
}

const MembersImportExportControls = () => {
  const dispatch = useDispatch()
  const fileInput = useRef(null)
  const [exporting, setExporting] = useState(false)
  const [importing, setImporting] = useState(false)

  const onExportMembers = () => {
    setExporting(true)
    dispatch(exportMembers()).then(
      () => setExporting(false),
      err => {
        console.log('Export members failed. Error: ', err)
        alert('Export failed. Please try again.')
        setExporting(false)
      }
    )
  }

  const onOpenImport = () => {
    if(fileInput.current) {
      fileInput.current.click()
    }
  }

  const onImportFile = event => {
    const input = event.target
    const file = input.files[0]

    if(!file) {
      return
    }

    const reader = new FileReader()

    reader.onload = () => {
      let parsed

      try {
        parsed = JSON.parse(reader.result)
      } catch (err) {
        alert('Import failed. The selected file must be valid JSON.')
        input.value = ''
        return
      }

      const membersToImport = Array.isArray(parsed) ? parsed : parsed.members

      if(!Array.isArray(membersToImport)) {
        alert('Import failed. Use a JSON array or an exported members file with a members array.')
        input.value = ''
        return
      }

      setImporting(true)
      dispatch(importMembers(membersToImport)).then(
        data => {
          const summary = data.summary
          alert(
            'Import complete.\n' +
            'Received: ' + summary.received + '\n' +
            'Created: ' + (summary.created || 0) + '\n' +
            'Updated: ' + summary.updated + '\n' +
            'Skipped: ' + summary.skipped + '\n' +
            'Missing clinic_id: ' + (summary.skipped_missing_clinic_id || 0) + '\n' +
            'Unchanged: ' + summary.skipped_unchanged + '\n' +
            'Failed: ' + summary.failed
          )
          setImporting(false)
          input.value = ''
        },
        err => {
          console.log('Import members failed. Error: ', err)
          alert('Import failed. Please confirm the file format and try again.')
          setImporting(false)
          input.value = ''
        }
      )
    }

    reader.onerror = () => {
      alert('Import failed. The selected file could not be read.')
      input.value = ''
    }

    reader.readAsText(file)
  }

  return (
    <Grid container justify="center" spacing={2}>
      <Grid item>
        <Button onClick={onExportMembers} variant="outlined" disabled={exporting || importing}>
          {exporting ? 'Exporting...' : 'Export Members'}
        </Button>
      </Grid>
      <Grid item>
        <Button onClick={onOpenImport} variant="outlined" disabled={exporting || importing}>
          {importing ? 'Importing...' : 'Import Members'}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          onChange={onImportFile}
          style={{display: 'none'}}
        />
      </Grid>
    </Grid>
  )
}

const ManageMembers = ({members}) => {
  const [filteredSearchMembers, setFilteredSearchMembers] = useState([])
  const [filteredMembers, setFilteredMembers] = useState(filteredSearchMembers)
  const previousMemberCount = useRef(0)

  useEffect( () => {
    if(members && members.length !== 0 ) {
      const map = new Map(members.map(m => [m._id, m]))
      const previousCount = previousMemberCount.current
      const updateFilteredMembers = (prev, members, map) => {
        if (prev.length === 0 || prev.length === previousCount) return members;
        return prev.map(m => map.get(m._id) || m).filter(m => map.has(m._id));
      };
      setFilteredSearchMembers(prev => updateFilteredMembers(prev, members, map));
      setFilteredMembers(prev => updateFilteredMembers(prev, members, map));
    }
    previousMemberCount.current = members ? members.length : 0
  }, [members])

  const onApplySearch = quickSearch => {
    const query = quickSearch.toLowerCase()
    const membersNew = members.filter(member => [
      member.clinic_id,
      member.clinic_name,
      member.account_number,
      member.api_endpoint,
      member.username,
      member.email,
      member.notification_emails,
      member.customer?.name,
      member.street1,
      member.city,
      member.state,
      member.zip
    ].some(value => String(value == null ? '' : value).toLowerCase().includes(query)))
    setFilteredSearchMembers(membersNew)
    setFilteredMembers(membersNew)
  }

  //https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
  const onSortColumn = (field, order) => {
    const isAsc = order === 'asc'
    const sortedMembers = members.sort((a, b) => {
      if(!a[field] && !b[field])
        return 0
      if(!b[field])
        return isAsc ? -1 : 1;
      if(!a[field])
        return isAsc ? 1 : -1;

      const fieldA = a[field].toUpperCase(); // ignore upper and lowercase
      const fieldB = b[field].toUpperCase(); // ignore upper and lowercase
      if (fieldA < fieldB) {
        return isAsc ? -1 : 1;
      } else if (fieldA > fieldB) {
        return isAsc ? 1 : -1;
      }
      return 0; //fields are equal
    });
    setFilteredSearchMembers(sortedMembers)
    setFilteredMembers(sortedMembers)
    return isAsc ? 'desc' : 'asc'
  }

  return (
    <div className="o-wrapper">
      <Grid container direction="row" justify="space-between" alignItems="center" style={{paddingTop: "20px"}}>
        <Grid item><Typography variant="h5">Members</Typography></Grid>
        <Grid item>
          <Grid container justify="center" spacing={3}>
            <Grid item>
              <MembersImportExportControls />
            </Grid>
            <Grid item> 
              <ModalWrapper
                title="Add Member"
                buttonText="Add Member"
                width={800}
                showOk={false}
                component={AddMember}
              /> 
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/************************************************** Search and Filter *****************************************/}
      <Grid container justify="space-between" alignItems="center" style={{paddingTop: "20px", paddingBottom: "20px"}}>
        <Grid item><MembersSearchBar onApplySearch={onApplySearch}/></Grid>
        <Grid item><MemberFilterDrawer members={filteredSearchMembers} setMembers={setFilteredMembers}/></Grid>
      </Grid>

      {/************************************************** Table *****************************************/}
      <MembersTable members={filteredMembers} onSort={onSortColumn}/>
    </div>
  )
}

export default ManageMembers;
